import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { DescriptionList } from '@/components/ui/description-list'
import { PageHeader } from '@/components/ui/page-header'
import { SectionHeader } from '@/components/ui/section-header'
import {
  ContactDirectory,
  ProcessSteps,
  WelcomeIntro,
} from '@/features/intake/welcome-content'

/**
 * /getting-started — the Welcome Quick Start Guide, in the Hub. Pure static
 * content (no queries): the same material the welcome packet carries, kept
 * here so it survives the inbox. Shares its building blocks with the public
 * intake page (welcome-content.tsx).
 */

const SETUP_STEPS = [
  <>Complete the customer intake from your welcome email — billing details, day-to-day contacts, and ship-tos.</>,
  <>
    Want payment terms? Send the{' '}
    <GuideLink to="/credit">credit application</GuideLink> and Green Line&rsquo;s credit team takes
    it from there.
  </>,
  <>Line up your first-order details with your rep — product, quantity, and requested ship date.</>,
]

const DOCUMENT_ITEMS = [
  'Technical Data Sheet (TDS)',
  'Safety Data Sheet (SDS)',
  'Certificate of Analysis (COA) — with every lot',
  'Regulatory support for your filings',
]

export function GettingStartedPage() {
  return (
    <div className="mx-auto max-w-form">
      <PageHeader
        eyebrow="Quick start guide"
        title="Getting started with Green Line"
        description="How onboarding, samples, ordering, and support run — and who to write at every step."
      />
      <WelcomeIntro className="mt-6 max-w-xl" />

      <section aria-labelledby="gs-process-heading" className="mt-10">
        <SectionHeader id="gs-process-heading" title="How working together runs" />
        <ProcessSteps variant="full" className="mt-4" />
      </section>

      <section aria-labelledby="gs-setup-heading" className="mt-10">
        <SectionHeader id="gs-setup-heading" title="Getting set up" />
        <ol className="mt-4 space-y-2.5">
          {SETUP_STEPS.map((step, index) => (
            <li key={index} className="flex items-baseline gap-3">
              <span aria-hidden="true" className="w-4 shrink-0 font-mono text-[0.6875rem] text-mute">
                {index + 1}
              </span>
              <span className="text-sm leading-relaxed text-inkMid">{step}</span>
            </li>
          ))}
        </ol>
      </section>

      <section aria-labelledby="gs-samples-heading" className="mt-10">
        <SectionHeader id="gs-samples-heading" title="Samples" />
        <p className="mt-3 text-sm leading-relaxed text-inkMid">
          When you ask for a sample, share the end application, your estimated annual volume, and
          the project timeline — that&rsquo;s what lets us ship the right sample with the right
          documentation package the first time.
        </p>
      </section>

      <section aria-labelledby="gs-ordering-heading" className="mt-10">
        <SectionHeader id="gs-ordering-heading" title="Placing an order" />
        <p className="mt-3 text-sm leading-relaxed text-inkMid">Every purchase order needs four things:</p>
        <Card className="mt-3">
          <DescriptionList
            columns={2}
            items={[
              { label: 'Product', value: 'Product name and code' },
              { label: 'Quantity', value: 'In kilograms, with pack size' },
              { label: 'Ship date', value: 'Your requested ship date' },
              { label: 'Addresses', value: 'Ship-to and bill-to' },
            ]}
          />
        </Card>
        <p className="mt-3 text-sm leading-relaxed text-inkMid">
          Every order is confirmed within 48 hours, and from there it runs:
        </p>
        <p className="mt-2 font-mono text-[0.8125rem] leading-relaxed text-inkMid">
          PO → confirmation → allocation or production → shipment + invoicing
        </p>
      </section>

      <section aria-labelledby="gs-leadtimes-heading" className="mt-10">
        <SectionHeader id="gs-leadtimes-heading" title="Lead times" />
        <ul className="mt-3 space-y-2">
          <GuideBullet>
            <span className="font-medium text-ink">In stock</span> — ships promptly after your
            order is confirmed.
          </GuideBullet>
          <GuideBullet>
            <span className="font-medium text-ink">Made to order</span> — runs on the production
            lead time; your order confirmation carries the date.
          </GuideBullet>
        </ul>
      </section>

      <section aria-labelledby="gs-pricing-heading" className="mt-10">
        <SectionHeader id="gs-pricing-heading" title="Pricing" />
        <p className="mt-3 text-sm leading-relaxed text-inkMid">
          Pricing comes through quotes and is confirmed on every order confirmation. Annual
          forecasts help us secure supply and hold your pricing — share them when you can.
        </p>
      </section>

      <section aria-labelledby="gs-documentation-heading" className="mt-10">
        <SectionHeader id="gs-documentation-heading" title="Documentation" />
        <p className="mt-3 text-sm leading-relaxed text-inkMid">On request, for every product:</p>
        <ul className="mt-3 space-y-2">
          {DOCUMENT_ITEMS.map((item) => (
            <GuideBullet key={item}>{item}</GuideBullet>
          ))}
        </ul>
      </section>

      <section aria-labelledby="gs-directory-heading" className="mt-10">
        <SectionHeader id="gs-directory-heading" title="Who to write" />
        <ContactDirectory className="mt-4" />
      </section>

      <section aria-labelledby="gs-after-heading" className="mt-10">
        <SectionHeader id="gs-after-heading" title="After your first order" />
        <p className="mt-3 text-sm leading-relaxed text-inkMid">
          Order confirmations, shipment tracking, and invoices all live here in the Hub — start
          with <GuideLink to="/orders">Orders</GuideLink> and{' '}
          <GuideLink to="/invoices">Invoices</GuideLink>. Reorders can go straight in as{' '}
          <GuideLink to="/purchasing/new">purchase order requests</GuideLink>.
        </p>
      </section>

      <p className="manifest-rail mt-12 pl-4 text-sm leading-relaxed text-inkMid">
        Consistent supply, honest lead times, and documentation you can count on — that&rsquo;s
        the partnership we&rsquo;re committing to. Welcome to Green Line.
      </p>
    </div>
  )
}

function GuideBullet({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-baseline gap-3">
      <span aria-hidden="true" className="w-4 shrink-0 text-center font-mono text-xs text-mute">
        ·
      </span>
      <span className="text-sm leading-relaxed text-inkMid">{children}</span>
    </li>
  )
}

function GuideLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="rounded-sm text-brand underline-offset-4 transition-colors duration-[180ms] ease-editorial hover:underline"
    >
      {children}
    </Link>
  )
}
